import { useEffect, useMemo, useRef, useState } from 'react';
import { ManageLayout } from '../../components/ManageLayout';
import { MuscleMapInspector } from '../../components/MuscleMapInspector';
import css from './Muscles.module.css';

interface MuscleGroup {
  id: string;
  name: string;
  region: string | null;
  exercise_count: number;
}

// Canonical list of regions a muscle group can be assigned to. Cardio and
// Stretching are exercise-level (exercise_type) and intentionally absent.
const REGION_OPTIONS = [
  'Abdomen',
  'Arms',
  'Back',
  'Buttocks',
  'Chest',
  'Legs',
  'Shoulders',
] as const;

interface Link {
  exercise_id: string;
  exercise_name: string;
  muscle_id: string;
  is_primary: number;
}

type View = 'by-muscle' | 'by-exercise';

export function ManageMuscles() {
  const [groups, setGroups] = useState<MuscleGroup[] | null>(null);
  const [links, setLinks] = useState<Link[] | null>(null);
  const [view, setView] = useState<View>('by-muscle');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [selectedExerciseId, setSelectedExerciseId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    setError(null);
    try {
      const res = await fetch('/api/manage/muscles');
      const data = (await res.json()) as { groups: MuscleGroup[]; links: Link[] };
      setGroups(data.groups);
      setLinks(data.links);
    } catch {
      setError("couldn't load muscle data");
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  const linksByMuscle = useMemo(() => {
    const m = new Map<string, Link[]>();
    for (const l of links ?? []) {
      let list = m.get(l.muscle_id);
      if (!list) {
        list = [];
        m.set(l.muscle_id, list);
      }
      list.push(l);
    }
    return m;
  }, [links]);

  const linksByExercise = useMemo(() => {
    const m = new Map<string, { name: string; links: Link[] }>();
    for (const l of links ?? []) {
      let entry = m.get(l.exercise_id);
      if (!entry) {
        entry = { name: l.exercise_name, links: [] };
        m.set(l.exercise_id, entry);
      }
      entry.links.push(l);
    }
    return m;
  }, [links]);

  const exerciseList = useMemo(() => {
    const arr = [...linksByExercise.entries()]
      .map(([id, v]) => ({ id, name: v.name, links: v.links }))
      .sort((a, b) => a.name.localeCompare(b.name));
    if (!query.trim()) return arr;
    const q = query.toLowerCase();
    return arr.filter((e) => e.name.toLowerCase().includes(q));
  }, [linksByExercise, query]);

  const filteredGroups = useMemo(() => {
    if (!groups) return [];
    if (!query.trim()) return groups;
    const q = query.toLowerCase();
    return groups.filter((g) => g.name.toLowerCase().includes(q));
  }, [groups, query]);

  return (
    <ManageLayout title="Muscles">
      {error && <div className={css.error}>{error}</div>}

      <div className={css.toolbar}>
        <button
          type="button"
          className={view === 'by-muscle' ? `${css.viewSwitch} ${css.viewSwitchActive}` : css.viewSwitch}
          onClick={() => setView('by-muscle')}
        >
          by muscle
        </button>
        <button
          type="button"
          className={view === 'by-exercise' ? `${css.viewSwitch} ${css.viewSwitchActive}` : css.viewSwitch}
          onClick={() => setView('by-exercise')}
        >
          by exercise
        </button>
        <input
          type="search"
          placeholder={view === 'by-muscle' ? 'search muscles…' : 'search exercises…'}
          className={css.search}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className={css.split}>
        <div className={css.splitList}>
          {!groups || !links ? (
            <div style={{ color: '#666', padding: 24, textAlign: 'center' }}>loading…</div>
          ) : view === 'by-muscle' ? (
            <ul className={css.list}>
              {filteredGroups.map((g) => (
                <MuscleRow
                  key={g.id}
                  group={g}
                  open={openId === g.id}
                  links={linksByMuscle.get(g.id) ?? []}
                  onToggle={() => setOpenId(openId === g.id ? null : g.id)}
                  onRenamed={reload}
                />
              ))}
            </ul>
          ) : (
            <ul className={css.byExerciseList}>
              {exerciseList.map((ex) => {
                const isSelected = selectedExerciseId === ex.id;
                return (
                  <li key={ex.id}>
                    <button
                      type="button"
                      className={`${css.exerciseRow}${isSelected ? ` ${css.exerciseRowActive}` : ''}`}
                      onClick={() => setSelectedExerciseId(isSelected ? null : ex.id)}
                    >
                      <span className={css.exerciseName}>{ex.name}</span>
                      <div className={css.muscleChips}>
                        {ex.links.length === 0 ? (
                          <span className={css.empty}>no muscle tags</span>
                        ) : (
                          ex.links
                            .slice()
                            .sort((a, b) => b.is_primary - a.is_primary)
                            .map((l) => {
                              const g = groups.find((x) => x.id === l.muscle_id);
                              return (
                                <span
                                  key={l.muscle_id}
                                  className={`${css.chip} ${l.is_primary ? css.chipPrimary : css.chipSecondary}`}
                                >
                                  {g?.name ?? l.muscle_id}
                                </span>
                              );
                            })
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {groups && (
          <aside className={css.splitInspector}>
            <InspectorHint
              view={view}
              openId={openId}
              selectedExerciseId={selectedExerciseId}
              exerciseList={exerciseList}
              groups={groups}
            />
            <MuscleMapInspector
              activeMuscle={view === 'by-muscle' ? openId : null}
              exerciseMuscles={
                view === 'by-exercise' && selectedExerciseId
                  ? (linksByExercise.get(selectedExerciseId)?.links ?? []).map((l) => ({
                      muscle_id: l.muscle_id,
                      is_primary: l.is_primary,
                    }))
                  : undefined
              }
              muscles={groups}
              stack
            />
          </aside>
        )}
      </div>
    </ManageLayout>
  );
}

function MuscleRow({
  group,
  open,
  links,
  onToggle,
  onRenamed,
}: {
  group: MuscleGroup;
  open: boolean;
  links: Link[];
  onToggle: () => void;
  onRenamed: () => void;
}) {
  const primary = useMemo(() => links.filter((l) => l.is_primary), [links]);
  const secondary = useMemo(() => links.filter((l) => !l.is_primary), [links]);
  const [name, setName] = useState(group.name);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const savedTimer = useRef<number | null>(null);

  // Reset local state when group changes (e.g. after reload).
  useEffect(() => {
    setName(group.name);
  }, [group.name]);
  useEffect(() => {
    return () => {
      if (savedTimer.current != null) window.clearTimeout(savedTimer.current);
    };
  }, []);

  const isDirty = name.trim() !== group.name && name.trim() !== '';

  async function rename() {
    if (!isDirty) return;
    setError(null);
    setSaving(true);
    try {
      const res = await fetch(`/api/manage/muscles/${group.id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      });
      if (!res.ok) throw new Error(`rename failed: ${res.status}`);
      setSavedAt(Date.now());
      if (savedTimer.current != null) window.clearTimeout(savedTimer.current);
      savedTimer.current = window.setTimeout(() => setSavedAt(null), 2000);
      onRenamed();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'rename failed');
    } finally {
      setSaving(false);
    }
  }

  async function setRegion(next: string) {
    setError(null);
    setSaving(true);
    try {
      const res = await fetch(`/api/manage/muscles/${group.id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ region: next || null }),
      });
      if (!res.ok) throw new Error(`region update failed: ${res.status}`);
      onRenamed();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'region update failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <li className={css.row}>
      <div className={css.rowHeader} onClick={onToggle} role="button" tabIndex={0}>
        <span>
          <span className={css.name}>{group.name}</span>
          <span className={css.idHint}>{group.id}</span>
        </span>
        <span className={css.col}>
          {group.region ? (
            <span className={css.regionChip}>{group.region}</span>
          ) : (
            <span className={css.regionChipMissing}>no region</span>
          )}
        </span>
        <span className={css.col}>{primary.length} primary</span>
        <span className={css.col}>{secondary.length} sec</span>
        <span className={css.caret}>{open ? '▴' : '▾'}</span>
      </div>
      {open && (
        <div className={css.body}>
          {error && <div className={css.error}>{error}</div>}
          <div className={css.field}>
            <label className={css.label}>region</label>
            <select
              className={css.select}
              value={group.region ?? ''}
              onChange={(e) => void setRegion(e.target.value)}
              disabled={saving}
            >
              <option value="">— none —</option>
              {REGION_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div className={css.field}>
            <label className={css.label}>rename</label>
            <input
              className={css.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void rename();
              }}
            />
            <button
              type="button"
              className={css.btn}
              onClick={() => void rename()}
              disabled={saving || !isDirty}
              title={!isDirty && !saving ? 'no changes to save' : undefined}
            >
              {saving ? 'saving…' : savedAt ? 'saved ✓' : isDirty ? 'save' : 'saved'}
            </button>
          </div>
          <div className={css.bodyExercises}>
            <div>
              <h4 className={css.tierTitle}>Primary mover for ({primary.length})</h4>
              {primary.length === 0 ? (
                <div className={css.empty}>no exercises</div>
              ) : (
                <ul className={css.tierList}>
                  {primary
                    .slice()
                    .sort((a, b) => a.exercise_name.localeCompare(b.exercise_name))
                    .map((l) => (
                      <li key={l.exercise_id} className={css.tierItem}>
                        {l.exercise_name}
                      </li>
                    ))}
                </ul>
              )}
            </div>
            <div>
              <h4 className={css.tierTitle}>Secondary for ({secondary.length})</h4>
              {secondary.length === 0 ? (
                <div className={css.empty}>no exercises</div>
              ) : (
                <ul className={css.tierList}>
                  {secondary
                    .slice()
                    .sort((a, b) => a.exercise_name.localeCompare(b.exercise_name))
                    .map((l) => (
                      <li key={l.exercise_id} className={css.tierItem}>
                        {l.exercise_name}
                      </li>
                    ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </li>
  );
}

function InspectorHint({
  view,
  openId,
  selectedExerciseId,
  exerciseList,
  groups,
}: {
  view: View;
  openId: string | null;
  selectedExerciseId: string | null;
  exerciseList: { id: string; name: string; links: Link[] }[];
  groups: MuscleGroup[];
}) {
  if (view === 'by-muscle') {
    if (openId) {
      const name = groups.find((g) => g.id === openId)?.name ?? openId;
      return <p className={css.inspectorHint}>{name} highlighted below.</p>;
    }
    return <p className={css.inspectorHint}>Click a muscle on the left to highlight it.</p>;
  }
  // by-exercise
  if (selectedExerciseId) {
    const ex = exerciseList.find((e) => e.id === selectedExerciseId);
    const exName = ex?.name ?? selectedExerciseId;
    const primary = ex?.links.filter((l) => l.is_primary).length ?? 0;
    const secondary = ex?.links.filter((l) => !l.is_primary).length ?? 0;
    return (
      <p className={css.inspectorHint}>
        {exName} — <span style={{ color: '#ef4444' }}>{primary} primary</span>,{' '}
        <span style={{ color: '#7c2222' }}>{secondary} secondary</span>
      </p>
    );
  }
  return <p className={css.inspectorHint}>Click an exercise on the left to highlight its muscles.</p>;
}
